import { useState } from 'react';

import type { ActionIntent } from '../../services/agentDraftApi';

interface Database {
  id: string;
  name: string;
}

interface DatabasePickerMessageProps {
  databases: Database[];
  multi?: boolean;
  onSelect: (params: { displayMessage: string; intent: ActionIntent }) => void;
  onSkip: (params: { displayMessage: string }) => void;
}

export function DatabasePickerMessage({
  databases,
  multi = false,
  onSelect,
  onSkip,
}: DatabasePickerMessageProps) {
  const [singleId, setSingleId] = useState<string>('');
  const [multiIds, setMultiIds] = useState<string[]>([]);

  const toggleMulti = (id: string) => {
    setMultiIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const handleConfirm = () => {
    if (multi) {
      if (!multiIds.length) return;
      const names = databases
        .filter((d) => multiIds.includes(d.id))
        .map((d) => d.name)
        .join(', ');
      onSelect({
        displayMessage: `Quero usar: ${names}`,
        intent: { type: 'link_databases', payload: { databaseIds: multiIds } },
      });
      return;
    }
    if (!singleId) return;
    const db = databases.find((d) => d.id === singleId);
    onSelect({
      displayMessage: `Quero usar a base "${db?.name ?? ''}"`,
      intent: { type: 'link_database', payload: { databaseId: singleId } },
    });
  };

  const confirmEnabled = multi ? multiIds.length > 0 : Boolean(singleId);

  return (
    <div
      data-testid="database-picker-message"
      style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {databases.map((db) => {
        const isSelected = multi ? multiIds.includes(db.id) : singleId === db.id;
        return (
          <button
            key={db.id}
            onClick={() => (multi ? toggleMulti(db.id) : setSingleId(db.id))}
            data-testid={`database-picker-option-${db.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 8,
              border: `2px solid ${isSelected ? '#8b5cf6' : '#e5e7eb'}`,
              background: isSelected ? '#f5f3ff' : '#fff',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              color: '#374151',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: multi ? 4 : '50%',
                border: `2px solid ${isSelected ? '#8b5cf6' : '#d1d5db'}`,
                background: isSelected ? '#8b5cf6' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {multi && isSelected && (
                <span
                  style={{
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  ✓
                </span>
              )}
            </div>
            {db.name}
          </button>
        );
      })}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleConfirm}
          disabled={!confirmEnabled}
          data-testid="database-picker-confirm"
          style={{
            padding: '7px 14px',
            borderRadius: 8,
            border: 'none',
            background: confirmEnabled ? '#8b5cf6' : '#e5e7eb',
            color: confirmEnabled ? '#fff' : '#9ca3af',
            fontWeight: 600,
            fontSize: 14,
            cursor: confirmEnabled ? 'pointer' : 'not-allowed',
          }}
        >
          {multi ? 'Confirmar seleção' : 'Usar esta base'}
        </button>
        <button
          onClick={() => onSkip({ displayMessage: 'Não preciso de base de conhecimento por enquanto.' })}
          data-testid="database-picker-skip"
          style={{
            padding: '7px 14px',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            background: '#fff',
            color: '#6b7280',
            fontWeight: 500,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Pular
        </button>
      </div>
    </div>
  );
}
